import type { NotificationItem, ProductDetail, StageDefinition } from "@/types/domain"
import { demoBooks, demoBookSummary } from "@/data/demo-book-data"
import { demoPrintShops } from "@/data/demo-print-shop-data"

const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString()

export const stages: StageDefinition[] = [
  { id: "cadastro", name: "Cadastro", shortName: "CAD", position: 0 },
  { id: "editorial", name: "Editorial", shortName: "EDI", position: 1 },
  { id: "design", name: "Design", shortName: "DES", position: 2 },
  { id: "producao", name: "Produção", shortName: "PRO", position: 3 },
  { id: "recebido", name: "Recebido", shortName: "REC", position: 4 },
]

export const demoProducts: ProductDetail[] = [
  {
    id: "product-filosofia-jul-26",
    code: "FIL-2607",
    name: "Box Clube de Filosofia",
    referenceMonth: "2026-07",
    imprint: "Clube de Filosofia",
    owner: "Marina Rocha",
    status: "BLOCKED",
    itemCount: 4,
    completedItemCount: 1,
    criticalAgingDays: 8,
    blockedItemCount: 1,
    stageDistribution: { editorial: 1, design: 1, producao: 1, recebido: 1 },
    description: "Edição sobre ética, liberdade e responsabilidade no pensamento contemporâneo.",
    startedAt: daysAgo(24),
    pipelineVersion: "Filosofia v4",
    kitVersion: "Kit mensal v3",
    stages,
    subscribers: ["Marina Rocha", "André Sato", "Bianca Cruz"],
    links: [
      { id: "link-1", label: "Pasta do produto", url: "https://drive.google.com" },
      { id: "link-2", label: "Briefing editorial", url: "https://docs.google.com" },
    ],
    globalGates: [
      { id: "gate-1", label: "Identidade visual conjunta validada", required: false, status: "DONE", version: 2 },
      { id: "gate-2", label: "Kit piloto montado e aprovado", required: false, status: "PENDING", version: 1 },
    ],
    items: [
      {
        id: "item-main-book",
        name: "O utilitarismo",
        type: "BOOK",
        typeLabel: "Livro principal",
        currentStageId: "producao",
        status: "IN_PROGRESS",
        version: 7,
        enteredAt: daysAgo(5),
        applicableStageIds: ["cadastro", "editorial", "design", "producao", "recebido"],
        checklist: [
          { id: "check-book-proof", label: "Prova gráfica aprovada", required: false, status: "DONE", version: 2 },
          { id: "check-book-order", label: "Ordem de impressão confirmada", required: false, status: "DONE", version: 2 },
        ],
        metadataVersion: 1,
        bookEdition: demoBookSummary(demoBooks[0]),
        printShop: demoPrintShops[0],
      },
      {
        id: "item-guide",
        name: "Guia de leitura — Ética em movimento",
        type: "READING_GUIDE",
        typeLabel: "Livreto de auxílio",
        currentStageId: "editorial",
        status: "BLOCKED",
        blockedReason: "Aguardando aprovação do texto final pelo conselho editorial.",
        version: 4,
        enteredAt: daysAgo(8),
        applicableStageIds: ["cadastro", "editorial", "design", "producao", "recebido"],
        checklist: [
          { id: "check-guide-copy", label: "Texto final aprovado", required: false, status: "PENDING", version: 1 },
          { id: "check-guide-review", label: "Revisão ortográfica concluída", required: false, status: "PENDING", version: 1 },
          { id: "check-guide-notes", label: "Notas de leitura adicionais", required: false, status: "SKIPPED", reason: "Edição sem notas extras", version: 2 },
        ],
        metadataVersion: 1,
        bookEdition: demoBookSummary(demoBooks[2]),
        printShop: demoPrintShops[1],
      },
      {
        id: "item-bookmark",
        name: "Marca-página — Liberdade",
        type: "BOOKMARK",
        typeLabel: "Marca-página",
        currentStageId: "design",
        status: "IN_PROGRESS",
        version: 3,
        enteredAt: daysAgo(3),
        applicableStageIds: ["cadastro", "design", "producao", "recebido"],
        checklist: [
          { id: "check-bookmark-art", label: "Arte aprovada", required: false, status: "DONE", version: 2 },
          { id: "check-bookmark-spec", label: "Especificação de papel validada", required: false, status: "PENDING", version: 1 },
        ],
      },
      {
        id: "item-wrap",
        name: "Papel de embrulho — Trama clássica",
        type: "WRAPPING_PAPER",
        typeLabel: "Papel de embrulho",
        currentStageId: "recebido",
        status: "DONE",
        version: 8,
        enteredAt: daysAgo(1),
        applicableStageIds: ["cadastro", "design", "producao", "recebido"],
        checklist: [],
      },
    ],
  },
  {
    id: "product-literatura-ago-26",
    code: "LIT-2608",
    name: "Box Clube de Literatura",
    referenceMonth: "2026-08",
    imprint: "Clube de Literatura",
    owner: "Lucas Freire",
    status: "ACTIVE",
    itemCount: 4,
    completedItemCount: 0,
    criticalAgingDays: 6,
    blockedItemCount: 0,
    stageDistribution: { cadastro: 1, editorial: 2, design: 1 },
    description: "Uma edição dedicada à narrativa breve latino-americana.",
    startedAt: daysAgo(11),
    pipelineVersion: "Literatura v2",
    kitVersion: "Kit mensal v5",
    stages,
    subscribers: ["Lucas Freire", "Marina Rocha"],
    links: [],
    globalGates: [],
    items: [
      {
        id: "lit-book",
        name: "Antologia latino-americana",
        type: "BOOK",
        typeLabel: "Livro principal",
        currentStageId: "editorial",
        status: "IN_PROGRESS",
        version: 2,
        enteredAt: daysAgo(6),
        applicableStageIds: ["cadastro", "editorial", "design", "producao", "recebido"],
        checklist: [{ id: "lit-c1", label: "Seleção final aprovada", required: false, status: "DONE", version: 2 }],
        metadataVersion: 1,
        bookEdition: demoBookSummary(demoBooks[1]),
        printShop: demoPrintShops[0],
      },
      {
        id: "lit-guide",
        name: "Caderno de autores",
        type: "READING_GUIDE",
        typeLabel: "Livreto de auxílio",
        currentStageId: "editorial",
        status: "IN_PROGRESS",
        version: 2,
        enteredAt: daysAgo(4),
        applicableStageIds: ["cadastro", "editorial", "design", "producao", "recebido"],
        checklist: [{ id: "lit-c2", label: "Biografias revisadas", required: false, status: "PENDING", version: 1 }],
        metadataVersion: 1,
        bookEdition: demoBookSummary(demoBooks[2]),
      },
      {
        id: "lit-card",
        name: "Cartão do curador",
        type: "INSERT",
        typeLabel: "Impresso adicional",
        currentStageId: "design",
        status: "IN_PROGRESS",
        version: 3,
        enteredAt: daysAgo(2),
        applicableStageIds: ["cadastro", "design", "producao", "recebido"],
        checklist: [{ id: "lit-c3", label: "Arte validada", required: false, status: "DONE", version: 2 }],
      },
      {
        id: "lit-wrap",
        name: "Papel de embrulho",
        type: "WRAPPING_PAPER",
        typeLabel: "Papel de embrulho",
        currentStageId: "cadastro",
        status: "IN_PROGRESS",
        version: 1,
        enteredAt: daysAgo(1),
        applicableStageIds: ["cadastro", "design", "producao", "recebido"],
        checklist: [{ id: "lit-c4", label: "Formato definido", required: false, status: "PENDING", version: 1 }],
      },
    ],
  },
  {
    id: "product-historia-set-26",
    code: "HIS-2609",
    name: "Box História em Contexto",
    referenceMonth: "2026-09",
    imprint: "História em Contexto",
    owner: "Bianca Cruz",
    status: "DRAFT",
    itemCount: 3,
    completedItemCount: 0,
    criticalAgingDays: 0,
    blockedItemCount: 0,
    stageDistribution: {},
    description: "Produto em composição antes do início do fluxo.",
    pipelineVersion: "História v1",
    kitVersion: "Kit mensal v1",
    stages,
    subscribers: ["Bianca Cruz"],
    links: [],
    globalGates: [],
    items: [],
  },
]

export const demoNotifications: NotificationItem[] = [
  {
    id: "notice-1",
    title: "Livreto bloqueado em Editorial",
    body: "Aguardando aprovação do texto final pelo conselho editorial.",
    runId: "run-estoicismo-1",
    createdAt: daysAgo(0.15),
    read: false,
    kind: "BLOCKED",
  },
  {
    id: "notice-2",
    title: "Digest diário · 3 pontos de atenção",
    body: "Um item bloqueado e dois itens acima do aging esperado.",
    createdAt: daysAgo(0.4),
    read: false,
    kind: "DIGEST",
  },
  {
    id: "notice-3",
    title: "Papel de embrulho recebido",
    body: "O último lote foi marcado como recebido.",
    runId: "run-estoicismo-1",
    createdAt: daysAgo(1),
    read: true,
    kind: "COMPLETED",
  },
]
